<template>
  <Transition name="fade">
    <Dialog
      v-if="show"
      :title="`New ${activeRecordTypeLabel || 'record'} — order #${orderNo}`"
      :show-cancel-button="false"
      :show-save-button="false"
      :custom-footer="true"
      :show-footer="true"
      :show-close-button="true"
      @closeEvent="emit('cancel')"
    >
      <template #modal-content>
        <div class="flex flex-col gap-6 text-sm">
          <p class="text-xs text-gray-500">
            Blocks the order type change until valid. Captured now as structured data, so
            failure cause, responsibility and mitigation are on the order rather than
            reconstructed from email months later.
          </p>

          <!-- STEP 1 — the gate -->
          <section class="flex flex-col gap-3">
            <StepHeading :number="1" title="Is this actually a failure?" />
            <p class="text-xs text-gray-500">
              Asked first, because it decides whether the order counts against anyone.
              Answering "no" closes the record here.
            </p>

            <div class="flex flex-col gap-2">
              <label
                v-for="type in availableRecordTypes"
                :key="type.value"
                class="flex cursor-pointer gap-3 rounded-md border p-3"
                :class="answers.record_type === type.value
                  ? 'border-[#00b087] bg-[#00b087]/5'
                  : 'border-gray-200 hover:border-gray-300'"
              >
                <input
                  v-model="answers.record_type"
                  type="radio"
                  :value="type.value"
                  class="mt-1"
                  @change="onRecordTypeChange"
                />
                <span class="flex flex-col">
                  <span class="font-medium text-gray-800">{{ type.label }}</span>
                  <span class="text-xs text-gray-500">{{ type.helperText }}</span>
                </span>
              </label>
            </div>

            <!-- Non-failure path: one line, then done. Steps 2-5 are not asked. -->
            <div
              v-if="answers.record_type && !isFailure"
              class="flex flex-col gap-2 rounded-md border border-amber-200 bg-amber-50 p-3"
            >
              <p class="text-xs text-amber-900">
                <strong>This will not count as a failure.</strong>
                Give a one-line reason and submit — the remaining questions are skipped.
              </p>
              <Field label="Reason" required>
                <input
                  v-model="answers.not_failure_reason"
                  type="text"
                  placeholder="e.g. Customer ordered 12 more of the same kit — original shipped clean"
                  class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none"
                />
              </Field>
            </div>
          </section>

          <template v-if="isFailure">
            <!-- STEP 2 -->
            <section class="flex flex-col gap-3 border-t border-gray-100 pt-5">
              <StepHeading :number="2" title="Link and ownership" />
              <div class="grid gap-4 md:grid-cols-2">
                <Field
                  label="Original order number"
                  required
                  hint="Must resolve to an existing order. Free text is rejected."
                  :error="errors.original_order_id"
                >
                  <input v-model="answers.original_order_id" type="text" inputmode="numeric" placeholder="e.g. 26171" class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none" />
                </Field>

                <Field
                  label="Recorded against"
                  required
                  hint="The last two options route the record to the Lead Order Administrator instead of leaving it ownerless."
                >
                  <select v-model="answers.attribution_target" class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none">
                    <option v-for="t in attributionTargets" :key="t.value" :value="t.value">{{ t.label }}</option>
                  </select>
                </Field>

                <Field
                  v-if="answers.attribution_target === 'other'"
                  label="Which order admin"
                  required
                  hint="Type to search by name."
                  :error="errors.attributed_admin_id"
                >
                  <!--
                    A searchable list, not a user id. Nobody remembers admin ids, and asking
                    for one guarantees the wrong person gets a failure recorded against them
                    — which is the exact problem this questionnaire exists to fix.
                    Same source as the order detail's admin pickers, so the options match
                    what an admin already sees elsewhere.
                  -->
                  <Select
                    append-to-body
                    v-model="answers.attributed_admin_id"
                    :options="orderAdminOptions"
                    label="name"
                    :reduce="(admin) => admin.id"
                    :clearable="false"
                    placeholder="Search order admins…"
                    class="w-full text-sm"
                  />
                </Field>

                <Field
                  label="Same failure on other orders"
                  hint="Comma-separated, 4–6 digits each. Copied to every order listed, so one QC rejection covering six orders is filed once."
                  :error="errors.also_affects"
                >
                  <input v-model="answers.also_affects" type="text" placeholder="e.g. 25841, 25874, 25875" class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none" />
                </Field>
              </div>
            </section>

            <!-- STEP 3 -->
            <section class="flex flex-col gap-3 border-t border-gray-100 pt-5">
              <StepHeading :number="3" title="What went wrong" />
              <div class="grid gap-4 md:grid-cols-2">
                <Field label="Failure category" required :error="errors.failure_category">
                  <select v-model="answers.failure_category" class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none">
                    <option value="">Select…</option>
                    <option v-for="c in availableFailureCategories" :key="c.value" :value="c.value">{{ c.label }}</option>
                  </select>
                </Field>

                <Field label="Stage detected" required hint="Shows where the net leaks, not just who to bill." :error="errors.stage_detected">
                  <select v-model="answers.stage_detected" class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none">
                    <option value="">Select…</option>
                    <option v-for="s in failureStages" :key="s.value" :value="s.value">{{ s.label }}</option>
                  </select>
                </Field>

                <Field
                  v-if="answers.failure_category === 'other'"
                  label="Describe the issue"
                  required
                  hint="In your own words, e.g. &quot;burn mark on left sleeve&quot;."
                  :error="errors.failure_category_other"
                  class="md:col-span-2"
                >
                  <input v-model="answers.failure_category_other" type="text" class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none" />
                </Field>

                <Field
                  label="What happened"
                  required
                  :hint="`Minimum ${FAILURE_DESCRIPTION_MIN_LENGTH} characters. One or two sentences — this is the field that replaces the email.`"
                  :error="errors.failure_description"
                  class="md:col-span-2"
                >
                  <textarea v-model="answers.failure_description" rows="3" class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none"></textarea>
                </Field>

                <Field label="Date identified" required :error="errors.date_identified">
                  <input v-model="answers.date_identified" type="date" class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none" />
                </Field>

                <Field
                  label="Defective pieces, if different"
                  hint="Leave blank unless they differ — the remake's own quantity is used otherwise."
                >
                  <input v-model="answers.defective_qty_override" type="number" min="0" max="9999" class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none" />
                </Field>
              </div>
            </section>

            <!-- STEP 4 -->
            <section class="flex flex-col gap-3 border-t border-gray-100 pt-5">
              <StepHeading :number="4" title="Who was responsible" />
              <p class="text-xs text-gray-500">
                Recorded by you now — not inferred from wording afterwards.
              </p>
              <div class="grid gap-4 md:grid-cols-2">
                <Field
                  label="Culprit"
                  required
                  hint="If the cause genuinely is not known yet, say so. A guess is worse than an open record."
                  :error="errors.culprit"
                >
                  <select v-model="answers.culprit" class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none">
                    <option value="">Select…</option>
                    <option v-for="c in culprits" :key="c.value" :value="c.value">{{ c.label }}</option>
                  </select>
                </Field>

                <Field label="Who bears the cost" required hint="Separate from blame — the factory can be at fault while we absorb the cost." :error="errors.cost_bearer">
                  <select v-model="answers.cost_bearer" class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none">
                    <option value="">Select…</option>
                    <option v-for="c in costBearers" :key="c.value" :value="c.value">{{ c.label }}</option>
                  </select>
                </Field>

                <Field
                  v-if="answers.culprit === 'custimoo'"
                  label="Which part of Custimoo"
                  required
                  hint="&quot;Administrative&quot; is where an order-admin error belongs."
                  :error="errors.culprit_subcategory"
                >
                  <select v-model="answers.culprit_subcategory" class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none">
                    <option value="">Select…</option>
                    <option v-for="c in culpritSubcategories" :key="c.value" :value="c.value">{{ c.label }}</option>
                  </select>
                </Field>

                <template v-if="answers.culprit === 'pending_investigation'">
                  <Field label="Investigation owner" required :error="errors.investigation_owner">
                    <select v-model="answers.investigation_owner" class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none">
                      <option value="">Select…</option>
                      <option v-for="t in responsibleTeams" :key="t.value" :value="t.value">{{ t.label }}</option>
                    </select>
                  </Field>
                  <Field label="Investigation due" hint="Defaults to the end of this month. Must be closed before the month is signed off.">
                    <input v-model="answers.investigation_due_date" type="date" class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none" />
                  </Field>
                </template>
              </div>
            </section>

            <!-- STEP 5 -->
            <section class="flex flex-col gap-3 border-t border-gray-100 pt-5">
              <StepHeading :number="5" title="What stops it happening again" />
              <p class="text-xs text-gray-500">
                The part no email ever captured. An owner and a date make it followable.
              </p>
              <div class="grid gap-4 md:grid-cols-2">
                <Field label="Immediate action taken" required :error="errors.immediate_action" class="md:col-span-2">
                  <input v-model="answers.immediate_action" type="text" placeholder="e.g. Remake raised with corrected print file; factory notified same day" class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none" />
                </Field>

                <Field
                  label="Preventive step"
                  required
                  hint="If nothing can prevent it, write &quot;None — one-off&quot; so the gap is visible rather than blank."
                  :error="errors.preventive_action"
                  class="md:col-span-2"
                >
                  <textarea v-model="answers.preventive_action" rows="2" placeholder="e.g. Add a letters-vs-patches check to the DQC checklist for all London SE orders." class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none"></textarea>
                </Field>

                <Field label="Owner of the preventive step">
                  <select v-model="answers.preventive_owner" class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none">
                    <option value="">Select…</option>
                    <option v-for="t in responsibleTeams" :key="t.value" :value="t.value">{{ t.label }}</option>
                  </select>
                </Field>

                <Field label="Target date">
                  <input v-model="answers.preventive_due_date" type="date" class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none" />
                </Field>
              </div>
            </section>
          </template>
        </div>
      </template>

      <template #footer>
        <div class="flex w-full items-center justify-between gap-3">
          <p v-if="firstError" class="text-xs text-red-500">{{ firstError }}</p>
          <p v-else class="text-xs text-gray-400">
            {{ isFailure ? 'Counts toward the remake rate.' : 'Will not count as a failure.' }}
          </p>
          <div class="flex gap-2">
            <button
              class="rounded-md bg-[#a0a0a0] px-3 py-2 text-[#3a3a3a]"
              @click="emit('cancel')"
            >
              Cancel
            </button>
            <button
              class="rounded-md bg-[#00b087] px-3 py-2 text-white disabled:cursor-not-allowed disabled:opacity-50"
              :disabled="!isValid"
              @click="submit"
            >
              Save and continue
            </button>
          </div>
        </div>
      </template>
    </Dialog>
  </Transition>
</template>

<script setup lang="ts">
/**
 * AB#20569 — the Remake Intake Questionnaire.
 *
 * Opened by PriceBreakdownModal when an order's type is being changed to one that counts as
 * a failure. Changing the type is a trigger in its own right: without that, an admin could
 * enter the order as a reorder, answer one field, then switch it to remake and skip the
 * form entirely.
 *
 * The validation here mirrors OrderFailureRecordService, which is where it is enforced.
 * This copy exists so the admin is told what is missing while typing, rather than getting a
 * 422 after pressing save — it is not the guard.
 */
import { computed, defineComponent, h, reactive, watch } from 'vue';
import Dialog from '@/components/Shared/Dialog.vue';
import Select from '@/components/Shared/Select.vue';
import { useMainStore } from '@/store';
import {
  ALSO_AFFECTS_PATTERN,
  FAILURE_DESCRIPTION_MIN_LENGTH,
  ORIGINAL_ORDER_PATTERN,
  attributionTargets,
  costBearers,
  culpritSubcategories,
  culprits,
  failureCategoriesForRecordType,
  failureStages,
  recordTypeCountsAsFailure,
  recordTypes,
  recordTypesForOrderType,
  responsibleTeams,
} from '@/utils/constants/remakeQuestionnaire';

const props = defineProps<{
  show: boolean;
  orderNo: string | number;
  /** Pre-filled when the admin already typed an original order number in the price modal. */
  originalOrderNo?: string | null;
  /** 'remake' when the chosen order type is a remake, 'reorder' for a reorder. */
  suggestedRecordType?: string | null;
  /**
   * The order type symbol the caller is about to save. Narrows the record types on offer so
   * the form cannot describe a different kind of order than the one being saved.
   */
  orderTypeSymbol?: string | null;
}>();

const emit = defineEmits<{
  (e: 'submit', answers: Record<string, unknown>): void;
  (e: 'cancel'): void;
}>();

const blank = () => ({
  record_type: props.suggestedRecordType ?? '',
  not_failure_reason: '',
  original_order_id: props.originalOrderNo ?? '',
  attribution_target: 'me',
  attributed_admin_id: '',
  also_affects: '',
  failure_category: '',
  failure_category_other: '',
  stage_detected: '',
  failure_description: '',
  date_identified: '',
  defective_qty_override: '',
  culprit: '',
  culprit_subcategory: '',
  investigation_owner: '',
  investigation_due_date: '',
  cost_bearer: '',
  immediate_action: '',
  preventive_action: '',
  preventive_owner: '',
  preventive_due_date: '',
});

const answers = reactive<Record<string, string>>(blank());

/**
 * Order admins for the "recorded against another admin" case.
 *
 * Read from the store rather than fetched here: the dashboard already loads this list
 * (store.orderAdmins, populated from role/users) and every other admin picker in the app
 * reads the same source. A second fetch would risk showing a different set of people than
 * the order detail does.
 */
const store = useMainStore();
const orderAdminOptions = computed(() => store.orderAdmins ?? []);

// Reset on each open, so a cancelled form never leaks into the next order.
watch(
  () => props.show,
  (open) => {
    if (!open) return;
    Object.assign(answers, blank());

    // The seed comes from the order type the caller picked, but the caller may have changed
    // it since. A pre-selected option that is no longer offered would sit there invisibly and
    // fail validation at the end, so it is dropped.
    if (
      answers.record_type
      && !availableRecordTypes.value.some((type) => type.value === answers.record_type)
    ) {
      answers.record_type = '';
    }
  },
);

/**
 * Only the record types that can honestly describe the order type being saved. The API
 * refuses the contradictory combinations (Remake + reorder above all); filtering here means
 * nobody fills in a form only to be refused at the end.
 */
const availableRecordTypes = computed(() => recordTypesForOrderType(props.orderTypeSymbol));

const isFailure = computed(() => recordTypeCountsAsFailure(answers.record_type));

const activeRecordTypeLabel = computed(
  () => recordTypes.find((t) => t.value === answers.record_type)?.label.split(' — ')[0] ?? '',
);

const availableFailureCategories = computed(() =>
  failureCategoriesForRecordType(answers.record_type),
);

/**
 * Clear the answers that depend on the record type.
 *
 * A warranty claim and a remake do not share a category list, so a category chosen for one
 * can be invalid for the other — and the backend would reject it. Clearing is more honest
 * than sending something that will be refused.
 */
function onRecordTypeChange(): void {
  answers.failure_category = '';
  answers.failure_category_other = '';
  if (!isFailure.value) return;
  answers.not_failure_reason = '';
}

const errors = computed<Record<string, string>>(() => {
  const e: Record<string, string> = {};

  if (!answers.record_type) {
    e.record_type = 'Choose what kind of record this is.';
    return e;
  }

  if (!isFailure.value) {
    if (!answers.not_failure_reason.trim()) {
      e.not_failure_reason = 'A one-line reason is required.';
    }
    return e;
  }

  if (!ORIGINAL_ORDER_PATTERN.test(answers.original_order_id.trim())) {
    e.original_order_id = 'An order number of 4 to 6 digits.';
  }
  if (answers.attribution_target === 'other' && !answers.attributed_admin_id) {
    e.attributed_admin_id = 'Say which order admin.';
  }
  if (answers.also_affects.trim() && !ALSO_AFFECTS_PATTERN.test(answers.also_affects.trim())) {
    e.also_affects = 'Comma-separated order numbers, 4 to 6 digits each.';
  }
  if (!answers.failure_category) {
    e.failure_category = 'Choose a category.';
  }
  if (answers.failure_category === 'other' && !answers.failure_category_other.trim()) {
    e.failure_category_other = 'Describe the issue in your own words.';
  }
  if (!answers.stage_detected) {
    e.stage_detected = 'Choose where it was detected.';
  }
  if (answers.failure_description.trim().length < FAILURE_DESCRIPTION_MIN_LENGTH) {
    e.failure_description = `At least ${FAILURE_DESCRIPTION_MIN_LENGTH} characters.`;
  }
  if (!answers.date_identified) {
    e.date_identified = 'When was it identified?';
  }
  if (!answers.culprit) {
    e.culprit = 'Choose who was responsible, or that it is not yet established.';
  }
  if (answers.culprit === 'custimoo' && !answers.culprit_subcategory) {
    e.culprit_subcategory = 'Say which part of Custimoo.';
  }
  if (answers.culprit === 'pending_investigation' && !answers.investigation_owner) {
    e.investigation_owner = 'An unresolved cause needs an owner.';
  }
  if (!answers.cost_bearer) {
    e.cost_bearer = 'Choose who bears the cost.';
  }
  if (!answers.immediate_action.trim()) {
    e.immediate_action = 'What was done about this order?';
  }
  if (!answers.preventive_action.trim()) {
    e.preventive_action = 'What stops it happening again? Write "None — one-off" if nothing can.';
  }

  return e;
});

const isValid = computed(() => Object.keys(errors.value).length === 0);
const firstError = computed(() => Object.values(errors.value)[0] ?? null);

function submit(): void {
  if (!isValid.value) return;

  // Empty strings are dropped rather than sent: the backend treats a blank optional field
  // and an absent one the same way, and sending '' for a date column is a 422.
  const payload: Record<string, unknown> = {};
  Object.entries(answers).forEach(([key, value]) => {
    const trimmed = typeof value === 'string' ? value.trim() : value;
    if (trimmed !== '' && trimmed !== null && trimmed !== undefined) {
      payload[key] = trimmed;
    }
  });

  emit('submit', payload);
}

/**
 * Two small presentational helpers, defined here rather than in their own files because
 * they exist only to keep this template readable. They must be declared inside
 * <script setup> to be visible to the template above — exports from a separate <script>
 * block are not auto-registered.
 */
const StepHeading = defineComponent({
  name: 'StepHeading',
  props: { number: { type: Number, required: true }, title: { type: String, required: true } },
  setup(p) {
    return () =>
      h('div', { class: 'flex items-center gap-2' }, [
        h(
          'span',
          {
            class:
              'flex h-6 w-6 items-center justify-center rounded bg-[#00b087]/10 text-xs font-semibold text-[#00b087]',
          },
          String(p.number),
        ),
        h('h4', { class: 'text-base font-semibold text-gray-800' }, p.title),
      ]);
  },
});

const Field = defineComponent({
  name: 'QuestionnaireField',
  props: {
    label: { type: String, required: true },
    required: { type: Boolean, default: false },
    hint: { type: String, default: '' },
    error: { type: String, default: '' },
  },
  setup(p, { slots }) {
    return () =>
      h('div', { class: 'flex flex-col gap-1.5' }, [
        h('label', { class: 'text-sm font-medium text-gray-700' }, [
          p.label,
          p.required ? h('span', { class: 'text-red-500' }, ' *') : null,
        ]),
        slots.default?.(),
        p.error
          ? h('p', { class: 'text-xs text-red-500' }, p.error)
          : p.hint
            ? h('p', { class: 'text-xs text-gray-500' }, p.hint)
            : null,
      ]);
  },
});
</script>
